import React, { useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { BaseView, BaseText } from '@/components/base'
import { HeaderNavigation } from '@/components/HeaderNavigation'
import { UserProfileInfo } from '@/components/UserProfileInfo'
import { FriendTransactionsList } from '@/components/FriendTransactionsList'
import { ModalActions } from '@/components/ModalActions'
import { AppButton } from '@/components/AppButton'
import { AppIcons } from '@/components/AppIcons'
import { AlertDescription } from '@/components/AlertDescription'

import {
  useGetProfile,
  useAddUserToFavorites,
  useUnfavoriteUser,
  useBlockUser,
  useUnblockUser,
} from '@/domain/identity'
import { useUserStore } from '@/store/useUserStore'
import useOffsets from '@/hooks/useOffsets'

export const UserProfileScreen: React.FC = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const offsets = useOffsets()

  const [modalActionsIsVisible, setModalActionsIsVisible] = useState(false)
  // const [isBlockActionLoading, setIsBlockActionLoading] = useState(false)

  // Get current user data
  const myId = useUserStore((state) => state.userAuthenticated?.user_id)
  const isMySelfProfile = userId === myId

  // Get profile data from API
  const {
    data: profile,
    isLoading: profileIsLoading,
    error: profileError,
  } = useGetProfile({
    user_id: userId || '',
  })

  // Favorite/unfavorite mutations
  const { mutateAsync: favoriteUser, isPending: isLoadingFavoriteUser } =
    useAddUserToFavorites({
      userId: userId || '',
    })
  const { mutateAsync: unfavoriteUser, isPending: isLoadingUnfavoriteUser } =
    useUnfavoriteUser({
      userId: userId || '',
    })

  // Block/unblock mutations
  const { mutateAsync: blockUser } = useBlockUser({ userId: userId || '' })
  const { mutateAsync: unblockUser } = useUnblockUser({ userId: userId || '' })

  // Profile states
  const isBlocked = profile?.relationship === 'BLOCKED'
  const isFavorite = profile?.relationship === 'FAVORITE'
  const isVerified = profile?.verify_state === 'VERIFIED'
  const isInactive = profile?.state === 'INACTIVE'
  const isLoadingFavorite = isLoadingFavoriteUser || isLoadingUnfavoriteUser

  // Handle API errors
  React.useEffect(() => {
    if (profileError) {
      toast.error(t('api:errors.somethingWrong'))
    }
  }, [profileError, t])

  const handleError = useCallback(() => {
    toast.error(t('api:errors.somethingWrong'))
  }, [t])

  const handleUserAction = useCallback(
    async (action: () => Promise<void>) => {
      try {
        // setIsBlockActionLoading(true)
        await action()
      } catch {
        handleError()
      } finally {
        // setIsBlockActionLoading(false)
      }
    },
    [handleError],
  )

  const handleBlockUser = useCallback(async () => {
    await handleUserAction(blockUser)
    setModalActionsIsVisible(false)
  }, [blockUser, handleUserAction])

  const handleUnblockUser = useCallback(async () => {
    await handleUserAction(unblockUser)
    setModalActionsIsVisible(false)
  }, [unblockUser, handleUserAction])

  const handleReportUser = useCallback(() => {
    navigate('/report-user', {
      state: {
        userId,
      },
    })
    setModalActionsIsVisible(false)
  }, [userId, navigate])

  const handleFavoriteUser = useCallback(async () => {
    await favoriteUser()
  }, [favoriteUser])

  const handleUnfavoriteUser = useCallback(async () => {
    await unfavoriteUser()
  }, [unfavoriteUser])

  const handleSendMoney = useCallback(() => {
    navigate('/send-request/with-user', {
      state: {
        id: profile?.user_id,
        name: `${profile?.first_name} ${profile?.last_name}`,
        username: profile?.username,
        avatar: profile?.avatar_path,
        type: 'send',
      },
    })
  }, [navigate, profile])

  const handleRequestMoney = useCallback(() => {
    navigate('/send-request/with-user', {
      state: {
        id: profile?.user_id,
        name: `${profile?.first_name} ${profile?.last_name}`,
        username: profile?.username,
        avatar: profile?.avatar_path,
        type: 'request',
      },
    })
  }, [navigate, profile])

  // Redirect if it's my own profile
  if (isMySelfProfile) {
    navigate('/profile/profile-settings')
    return null
  }

  // Show error if profile not found
  if (!profileIsLoading && !profile) {
    return (
      <BaseView flex={1} bgColor="background.screen" className="h-full">
        <HeaderNavigation title="" />
        <BaseView className="flex-1 justify-center items-center p-8">
          <BaseText
            fontSize={18}
            fontFamily="medium"
            color="label.default"
            className="text-center mb-4"
          >
            {t('api:userNotFound')}
          </BaseText>
          <AppButton type="secondaryGray" onPress={() => navigate(-1)}>
            {t('form:actions.okay')}
          </AppButton>
        </BaseView>
      </BaseView>
    )
  }

  const modalActions = [
    isBlocked
      ? {
          label: t('bottomSheets:modalActions.unblock'),
          action: handleUnblockUser,
          isDestructive: false,
        }
      : {
          label: t('bottomSheets:modalActions.block'),
          action: handleBlockUser,
          isDestructive: true,
        },
    {
      label: t('bottomSheets:modalActions.report'),
      action: handleReportUser,
      isDestructive: true,
    },
  ]

  return (
    <BaseView
      style={{
        // height: '100vh',
        paddingBottom: `${offsets.bottom + 50}px`,
      }}
      flex={1}
      bgColor="background.screen"
      className="relative"
    >
      {/* Header */}
      <HeaderNavigation
        onMoreOptionsPress={() => setModalActionsIsVisible(true)}
        isFixed={true}
        showBackground={true}
      />

      {/* Content */}
      <BaseView
        data-component="user-profile-content"
        flex={1}
        className="overflow-y-auto px-6"
        style={{
          paddingLeft: 13,
          paddingRight: 13,
          paddingTop: `${offsets.top + 70}px`, // Padding below fixed header
          paddingBottom: `${offsets.bottom}px`,
        }}
      >
        {/* User Profile Info - Centered */}
        <BaseView className="items-center py-0">
          <UserProfileInfo
            avatarUrl={profile?.avatar_path}
            firstName={profile?.first_name}
            lastName={profile?.last_name}
            username={profile?.username}
            phoneNumber={profile?.phone_number}
            avatarBg={profile?.avatarBg}
            isBlocked={isBlocked}
            isFavorite={isFavorite}
            isVerified={isVerified}
            isLoading={profileIsLoading}
          />
        </BaseView>

        {/* Your History Section */}
        <BaseView className="mt-8">
          {/* No recent payments */}
          {(!profile?.transactions || profile.transactions.length === 0) && (
            <BaseView className="flex-row items-center py-4">
              <BaseView className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center mr-3">
                <AppIcons name="clock" size={20} color="#9CA3AF" />
              </BaseView>
              <BaseText
                fontSize={16}
                fontFamily="regular"
                className="text-gray-400"
              >
                {t('selectContacts.noRecentPayments')}
              </BaseText>
            </BaseView>
          )}

          {/* Transactions List */}
          {profile?.transactions && profile.transactions.length > 0 && (
            <FriendTransactionsList
              skeletonActive={profileIsLoading}
              transactions={profile.transactions}
              userId={profile?.user_id || userId || ''}
              fullName={`${profile?.first_name} ${profile?.last_name}`}
            />
          )}
        </BaseView>

        {/* Pin/Unpin Button */}
        {!isBlocked && !isInactive && (
          <BaseView className="mt-4 h-14 mb-6 relative">
            <AppButton
              type="secondaryGray"
              onPress={isFavorite ? handleUnfavoriteUser : handleFavoriteUser}
              disabled={isLoadingFavorite}
              iconName={isFavorite ? 'unpin' : 'pin'}
              iconPosition="left"
              iconGap={8}
              label={
                isFavorite ? t('form:actions.unpin') : t('form:actions.pin')
              }
            />
            {/* Overlay loader */}
            {isLoadingFavorite && (
              <BaseView
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded-2xl"
                style={{ zIndex: 10 }}
              >
                <div
                  className="animate-spin rounded-full h-5 w-5 border-2 border-t-transparent border-white"
                  style={{
                    borderColor: '#ffffff',
                    borderTopColor: 'transparent',
                  }}
                />
              </BaseView>
            )}
          </BaseView>
        )}

        {/* Unverified User Warning */}
        {!isVerified && !profileIsLoading && !isInactive && (
          <AlertDescription
            description={t('unverifiedUserCautionMessage')}
            style={{ marginBottom: 24 }}
          />
        )}
      </BaseView>

      {/* Fixed Bottom Actions */}
      {!isBlocked && !isInactive && (
        <BaseView
          bgColor="background.screen"
          className="fixed flex left-0 right-0 p-4 flex-row gap-4 z-10"
          style={{
            flexDirection: 'row',
            boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
            bottom: `0`, // Use bottom instead of paddingBottom
            paddingBottom: `${offsets.bottom}px`,
          }}
        >
          <AppButton
            type="purpleGradientWithContour"
            onPress={handleRequestMoney}
            isSharedSpace
            label={t('form:actions.request')}
          />
          <AppButton
            type="primaryGradient"
            onPress={handleSendMoney}
            isSharedSpace
            label={t('form:actions.send')}
          />
        </BaseView>
      )}

      {/* Modal Actions */}
      <ModalActions
        isVisible={modalActionsIsVisible}
        onClose={() => setModalActionsIsVisible(false)}
        actions={modalActions}
        bottomOffset={offsets.bottom}
      />
    </BaseView>
  )
}
